import { z } from 'zod';

/**
 * Brazilian states (UF). 26 states + Distrito Federal (DF).
 * Fernando de Noronha is part of Pernambuco (PE) but uses a distinct timezone;
 * we expose 'FN' as a virtual UF only for timezone resolution — it is NOT a valid
 * state for `parking_lots.state`.
 */
export const ufList = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;

export type Uf = (typeof ufList)[number];

export const ufSet: ReadonlySet<Uf> = new Set(ufList);

export const isUf = (value: string): value is Uf => ufSet.has(value as Uf);

/**
 * Localizable state names for UI display.
 */
export const ufNames: Record<Uf, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins',
};

/**
 * Brazilian timezones. We only ever store/use these four — anything else is invalid.
 */
export const brTimezones = [
  'America/Rio_Branco',
  'America/Manaus',
  'America/Belem',
  'America/Sao_Paulo',
  'America/Noronha',
] as const;

export type BrTimezone = (typeof brTimezones)[number];

export const isBrTimezone = (value: string): value is BrTimezone =>
  (brTimezones as readonly string[]).includes(value);

/**
 * Maps each UF to its primary IANA timezone. The country has 4 mainland timezones
 * (UTC-2 through UTC-5); we use the dominant one per UF — Pará uses Belém even
 * though parts of the west of the state are on Manaus time, since that mirrors
 * the IBGE municipal-administrative split most parking lots will sit on.
 *
 * For Fernando de Noronha (UTC-2, Pernambuco), users must explicitly choose
 * the 'FN' option in the UI; the painel handles that as a separate selector
 * tied to PE.
 */
const STATE_TIMEZONE: Record<Uf, BrTimezone> = {
  AC: 'America/Rio_Branco',
  AL: 'America/Sao_Paulo',
  AP: 'America/Belem',
  AM: 'America/Manaus',
  BA: 'America/Sao_Paulo',
  CE: 'America/Sao_Paulo',
  DF: 'America/Sao_Paulo',
  ES: 'America/Sao_Paulo',
  GO: 'America/Sao_Paulo',
  MA: 'America/Sao_Paulo',
  MT: 'America/Manaus',
  MS: 'America/Sao_Paulo',
  MG: 'America/Sao_Paulo',
  PA: 'America/Belem',
  PB: 'America/Sao_Paulo',
  PR: 'America/Sao_Paulo',
  PE: 'America/Sao_Paulo',
  PI: 'America/Sao_Paulo',
  RJ: 'America/Sao_Paulo',
  RN: 'America/Sao_Paulo',
  RS: 'America/Sao_Paulo',
  RO: 'America/Manaus',
  RR: 'America/Manaus',
  SC: 'America/Sao_Paulo',
  SP: 'America/Sao_Paulo',
  SE: 'America/Sao_Paulo',
  TO: 'America/Sao_Paulo',
};

export const timezoneForState = (uf: Uf): BrTimezone => STATE_TIMEZONE[uf];

/**
 * Zod schema for state input. Accepts lowercase or uppercase; outputs always uppercase UF.
 */
export const stateSchema = z
  .string()
  .length(2, 'Estado deve ter 2 letras (UF).')
  .transform((s) => s.toUpperCase())
  .refine(isUf, 'UF inválida.');

export const brTimezoneSchema = z.enum(brTimezones);
